import React,{useState,useEffect,useContext,useRef,useCallback} from 'react'
import {Link} from 'react-router-dom'
import axios from 'axios'
import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
  MessageSeparator,
  TypingIndicator
} from "@chatscope/chat-ui-kit-react";
import moment from 'moment'
import {UserContext} from '../context/UserContext'
import Btn from '../ui/Btn'
import UiAvatar from '../ui/Avatar'
import './chat.css'
import {toPlain,toStored} from './chatText'

/*
 * One conversation, inside the Inbox.
 *
 * Who "me" is comes from the server (MeUUID on the room), not from the
 * session copy, which is empty for accounts that never had a chat identity
 * at sign-in.
 *
 * Sending saves first and only then tells the socket, so the player never
 * sees live a message that is not in the history. The message shows at once
 * as "Sending", and stays with a retry if the save fails. The socket echoes
 * our own messages back to us; those are skipped because they are already
 * on screen.
 */

const api=(path)=>process.env.REACT_APP_API_SERVER+path
const auth=()=>({headers:{'Authorization':'Bearer '+sessionStorage.getItem('mngapiToken')}})

function dayLabel(d){
  const m=moment(d)
  if (m.isSame(moment(),'day')) return 'Today'
  if (m.isSame(moment().subtract(1,'day'),'day')) return 'Yesterday'
  return m.format('dddd, D MMMM YYYY')
}

export default function ChatThread({roomUUID,serverTotal,onActivity,onBack}) {
  const {socket}=useContext(UserContext)
  const [room,setRoom]=useState(null)
  const [messages,setMessages]=useState([])
  const [state,setState]=useState('loading') // loading | ready | error | missing
  const [isTyping,setIsTyping]=useState(false)
  /* html feeds the editor back its own markup; text is what gets sent. */
  const [draft,setDraft]=useState({html:'',text:''})
  const roomRef=useRef(null)
  const seq=useRef(0)

  const markRead=useCallback(()=>{
    axios.get(api('manager/messages/update/'+roomUUID+'/me'),auth())
      .then(()=>{ if (onActivity) onActivity() })
      .catch(()=>{})
  },[roomUUID,onActivity])

  const load=useCallback(()=>{
    setState('loading')
    setRoom(null)
    setMessages([])
    setIsTyping(false)
    roomRef.current=null
    Promise.all([
      axios.get(api('manager/rooms/'+roomUUID+'/me'),auth()),
      axios.get(api('manager/messages/'+roomUUID),auth())
    ]).then(([r,m])=>{
      roomRef.current=r.data
      setRoom(r.data)
      setMessages((m.data.messages||[]).map(x=>({...x,key:'s'+(seq.current++)})))
      setState('ready')
      markRead()
    }).catch(err=>{
      setState(err&&err.response&&err.response.status===404?'missing':'error')
    })
  },[roomUUID,markRead])

  useEffect(()=>{
    load()
    if (socket) socket.emit('join-room',roomUUID)
  },[roomUUID,load,socket])

  /* The inbox polls; when it sees more messages in this room than are on
     screen, the socket missed some (it is down, or reconnecting). Fetch the
     history again and keep anything of ours still sending or failed. */
  useEffect(()=>{
    if (state!=='ready'||!serverTotal) return
    const saved=messages.filter(x=>!x.status).length
    if (serverTotal<=saved) return
    axios.get(api('manager/messages/'+roomUUID),auth()).then(m=>{
      const fetched=m.data.messages||[]
      /* A message still "sending" may already be in what came back. */
      const inFetched=(x)=>fetched.some(f=>f.SenderUUID===x.SenderUUID&&f.Message===x.Message)
      setMessages(list=>[
        ...fetched.map(x=>({...x,key:'s'+(seq.current++)})),
        ...list.filter(x=>x.status==='failed'||(x.status==='sending'&&!inFetched(x)))
      ])
      markRead()
    }).catch(()=>{})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[serverTotal])

  useEffect(()=>{
    if (!socket) return undefined
    const onMessage=(data)=>{
      const r=roomRef.current
      if (!r||!data||data.RoomUUID!==roomUUID) return
      if (data.SenderUUID===r.MeUUID) return
      setMessages(list=>[...list,{...data,key:'s'+(seq.current++)}])
      setIsTyping(false)
      markRead()
    }
    const onTyping=(who)=>{ const r=roomRef.current; if (r&&who===r.ChatWithUUID) setIsTyping(true) }
    const onStopped=(who)=>{ const r=roomRef.current; if (r&&who===r.ChatWithUUID) setIsTyping(false) }
    socket.on('receive-message',onMessage)
    socket.on('user-is-typing',onTyping)
    socket.on('user-stopped-typing',onStopped)
    return ()=>{
      socket.off('receive-message',onMessage)
      socket.off('user-is-typing',onTyping)
      socket.off('user-stopped-typing',onStopped)
    }
  },[socket,roomUUID,markRead])

  useEffect(()=>{
    const r=roomRef.current
    if (!socket||!r) return
    socket.emit(draft.text.trim().length>0?'me-typing':'me-stopped-typing',r.MeUUID,roomUUID)
  },[draft,socket,roomUUID])

  const deliver=(item)=>{
    const r=roomRef.current
    const obj={RoomUUID:roomUUID,SenderUUID:r.MeUUID,ReceiverUUID:r.ChatWithUUID,Message:item.Message}
    setMessages(list=>list.map(x=>x.key===item.key?{...x,status:'sending'}:x))
    axios.post(api('manager/messages/'),obj,auth())
      .then(res=>{
        if (res.data&&res.data.err==0){ // eslint-disable-line eqeqeq
          setMessages(list=>list.map(x=>x.key===item.key?{...x,status:null}:x))
          if (socket) socket.emit('send-message',obj)
          if (onActivity) onActivity()
        } else {
          setMessages(list=>list.map(x=>x.key===item.key?{...x,status:'failed'}:x))
        }
      })
      .catch(()=>setMessages(list=>list.map(x=>x.key===item.key?{...x,status:'failed'}:x)))
  }

  const send=(html,text,innerText)=>{
    const msg=String(innerText||text||'').trim()
    const r=roomRef.current
    if (!msg||!r) return
    const item={
      key:'l'+(seq.current++),
      RoomUUID:roomUUID,
      SenderUUID:r.MeUUID,
      ReceiverUUID:r.ChatWithUUID,
      Message:toStored(msg),
      DateCreated:new Date().toISOString(),
      status:'sending'
    }
    setDraft({html:'',text:''})
    setMessages(list=>[...list,item])
    deliver(item)
  }

  const peerName=room&&room.ChatWithName?room.ChatWithName:'Conversation'
  const myName=sessionStorage.getItem('userName')||'You'

  const rows=[]
  let lastDay=''
  messages.forEach(item=>{
    const day=moment(item.DateCreated).format('YYYY-MM-DD')
    if (day!==lastDay){
      rows.push(<MessageSeparator key={'d'+day+item.key} content={dayLabel(item.DateCreated)}/>)
      lastDay=day
    }
    const mine=room&&item.SenderUUID===room.MeUUID
    const time=moment(item.DateCreated).format('HH:mm')
    rows.push(
      <Message
        key={item.key}
        model={{
          message:toPlain(item.Message),
          /* Plain text, always. The kit's default is to render the message as
             HTML, which would let anything a player types run in this page. */
          type:'text',
          sentTime:time,
          sender:mine?myName:peerName,
          direction:mine?'outgoing':'incoming',
          position:'single'
        }}
      >
        <Message.Footer
          sentTime={item.status==='sending'?'Sending':item.status==='failed'?'Not sent':time}
        />
      </Message>
    )
    if (item.status==='failed'){
      rows.push(
        <div key={'f'+item.key} className="ib-failed">
          Not saved. <button type="button" onClick={()=>deliver(item)}>Send again</button>
        </div>
      )
    }
  })

  return (
    <div className="ib-thread">
      <div className="ib-thread-head">
        {onBack
          ?<button type="button" className="ib-back" onClick={onBack} aria-label="Back to conversations">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
          :null}
        {room
          ?<>
              <UiAvatar uuid={room.ChatWithUUID} name={peerName} size={36}/>
              <div className="ib-thread-who">
                <div className="ib-thread-name">{peerName}</div>
                <div className={isTyping?'chat-status':'chat-status is-off'}>
                  <span className="chat-dot"></span>
                  {isTyping?'Typing':(messages.length===1?'1 message':messages.length+' messages')}
                </div>
              </div>
              <Link className="btn-ui ib-profile" to={'/user/'+room.ChatWithUUID}>View profile</Link>
            </>
          :<div className="ib-thread-who"><span className="skeleton ib-sk-name"></span></div>}
      </div>

      {state==='loading'
        ?<div className="chat-sk" aria-hidden="true">
            <span className="skeleton sk-in"></span>
            <span className="skeleton sk-out"></span>
            <span className="skeleton sk-in sk-short"></span>
            <span className="skeleton sk-out"></span>
          </div>
        :null}

      {state==='error'||state==='missing'
        ?<div className="chat-notice is-error">
            <span className="chat-notice-row">
              <span>
                <b>{state==='missing'?'This conversation is not available.':'This conversation could not be loaded.'}</b>
                {state==='missing'?' It may have been closed.':' The server did not answer.'}
              </span>
              {state==='error'?<Btn onClick={load}>Try again</Btn>:null}
            </span>
          </div>
        :null}

      {state==='ready'
        ?<div className="chatx ib-chatx">
            <MainContainer>
              <ChatContainer>
                <MessageList typingIndicator={isTyping?<TypingIndicator content={peerName+' is typing'}/>:null}>
                  {rows.length===0
                    ?<MessageList.Content className="ib-thread-empty">Say hello to {peerName}.</MessageList.Content>
                    :rows}
                </MessageList>
                <MessageInput
                  value={draft.html}
                  onChange={(html,text,innerText)=>setDraft({html,text:innerText||text||''})}
                  onSend={send}
                  placeholder={'Message '+peerName}
                  attachButton={false}
                  sendDisabled={draft.text.trim().length===0}
                />
              </ChatContainer>
            </MainContainer>
          </div>
        :null}
    </div>
  )
}
